/** Pure helpers for the kid-first UI pieces (unit-tested in src/components/kid/__tests__). */

export type PictureTint = 'blue' | 'green' | 'peach' | 'lilac';
const TINTS: PictureTint[] = ['blue', 'green', 'peach', 'lilac'];

/** Full class names (Tailwind only ships classes it can see written out). */
export const TINT_CLASS: Record<PictureTint, string> = {
  blue: 'bg-kid-tint-blue',
  green: 'bg-kid-tint-green',
  peach: 'bg-kid-tint-peach',
  lilac: 'bg-kid-tint-lilac',
};

/** Same label → same tint, so a task keeps its colour everywhere. */
export function pictureTint(label: string): PictureTint {
  let h = 0;
  for (const ch of label || '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TINTS[h % TINTS.length];
}

/** First letter of the first word (skipping emoji/punctuation), upper-cased. "?" if none. */
export function pictureFallbackLetter(label: string): string {
  const m = (label || '').match(/\p{L}|\p{N}/u);
  return m ? m[0].toLocaleUpperCase() : '?';
}

/**
 * Only real image sources are rendered. Anything else — empty, or a raw catalogue key such as
 * "bathroom_spray" (seen in the design review) — gets the fallback tile instead of a broken image.
 */
export function usablePictureSrc(src: string | null | undefined): string | undefined {
  if (!src) return undefined;
  const s = src.trim();
  if (/^(https?:|data:image\/|blob:)/i.test(s) || s.startsWith('/')) return s;
  return undefined;
}

/**
 * Raw catalogue keys sometimes end up as a tool's name (e.g. "bathroom_spray" in test data).
 * Show them as words ("Bathroom spray") so a child never sees a code. Real names are untouched.
 */
export function humanizeKeyLabel(label: string): string {
  const s = (label || '').trim();
  if (!/^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(s)) return s;
  const words = s.split('_').join(' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Columns for the tool tiles on the tools-check and tidy-up screens, so a normal number of tools
 * fits on one screen without scrolling: phone (< 768 px), tablet portrait (768–1023), and tablet
 * landscape / computer (≥ 1024). Balanced rows: 5–6 tools on a wide screen become 3 + 3, not 4 + 2.
 */
export function toolGridColumns(count: number, viewportWidth: number): number {
  const n = Math.max(1, count);
  if (viewportWidth < 768) return n === 1 ? 1 : 2;
  if (viewportWidth < 1024) {
    if (n <= 3) return n;
    if (n === 4) return 2;
    return 3;
  }
  if (n <= 4) return n;
  if (n <= 6) return 3;
  return 4;
}
